import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalHeading,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  Button,
  Label,
  Input,
  Select,
  Option,
  Box,
  HelpText,
} from "@twilio-paste/core";
import { OperatorDto } from "../../dto/postLogin";
import { TeamDto } from "../../dto/team";
import { RolesDto } from "../../dto/roles";
import {
  CreateOperatorRequest,
  UpdateOperatorRequest,
  createOperator,
  updateOperator,
} from "../../api/adminApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (op: OperatorDto) => void;
  operator?: OperatorDto | null;
  teams: TeamDto[];
  roles: RolesDto[];
};

const OperatorFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSaved,
  operator,
  teams,
  roles,
}) => {
  const isEdit = !!operator;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (operator) {
      setName(operator.name);
      setEmail(operator.email);
      setTeamId(operator.teamId);
      setRoleId(operator.roleId);
      setIsActive(operator.isActive);
    } else {
      setName("");
      setEmail("");
      setTeamId(teams[0]?.id ?? "");
      setRoleId(roles[0]?.id ?? "");
      setIsActive(true);
    }
    setError(null);
  }, [operator, isOpen, teams, roles]);

  const handleSave = async () => {
    if (!name.trim() || !email.trim() || !teamId || !roleId) {
      setError("Tutti i campi sono obbligatori.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let saved: OperatorDto;
      if (isEdit && operator) {
        const req: UpdateOperatorRequest = { name, teamId, roleId, isActive };
        saved = await updateOperator(operator.id, req);
      } else {
        const req: CreateOperatorRequest = { name, email, teamId, roleId };
        saved = await createOperator(req);
      }
      onSaved(saved);
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ??
          e?.message ??
          "Errore durante il salvataggio."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      ariaLabelledby="operator-form-modal"
      isOpen={isOpen}
      onDismiss={onClose}
      size="default"
    >
      <ModalHeader>
        <ModalHeading as="h3">
          {isEdit ? "Modifica operatore" : "Nuovo operatore"}
        </ModalHeading>
      </ModalHeader>
      <ModalBody>
        <Box display="flex" flexDirection="column" rowGap="space40">
          <Box>
            <Label htmlFor="op-name" required>
              Nome
            </Label>
            <Input
              id="op-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box>
            <Label htmlFor="op-email" required>
              Email
            </Label>
            <Input
              id="op-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
            />
          </Box>
          <Box>
            <Label htmlFor="op-team" required>
              Team
            </Label>
            <Select
              id="op-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              {teams.map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Box>
          <Box>
            <Label htmlFor="op-role" required>
              Ruolo
            </Label>
            <Select
              id="op-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
            >
              {roles.map((r) => (
                <Option key={r.id} value={r.id}>
                  {r.name}
                </Option>
              ))}
            </Select>
          </Box>
          {isEdit && (
            <Box>
              <Label htmlFor="op-active">Stato</Label>
              <Select
                id="op-active"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
              >
                <Option value="true">Attivo</Option>
                <Option value="false">Disabilitato</Option>
              </Select>
            </Box>
          )}
          {error && <HelpText variant="error">{error}</HelpText>}
        </Box>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            {isEdit ? "Salva" : "Crea"}
          </Button>
        </ModalFooterActions>
      </ModalFooter>
    </Modal>
  );
};

export default OperatorFormModal;
